import { PropertiesStore } from '@hortplus/properties-react';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { Button, Modal } from 'react-bootstrap';
import LoadingOverlay from '../LoadingOverlay';
import { useStores } from '../stores/useStores';
import { dateFormatRegex } from '../utils';
import TrappingZoneSprayForm from './TrappingZoneSprayForm';

function EditTrappingZoneSprayModal(props) {
  const { traplogStore } = useStores();

  const databaseYear = traplogStore.selectedDatabaseYear() - 1;

  const hide = () => props.setTrappingZoneSpray(null);

  if (!props.trappingZoneSpray) return null;
  else {
    const trappingZone = traplogStore.trappingZones.find(
      (trappingZone) =>
        trappingZone.id === props.trappingZoneSpray.trapping_zone_id
    );

    return (
      <Modal backdrop='static' show onHide={() => hide()}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Spray Application {trappingZone.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{
              // react-select values
              sprayProduct: {
                value: props.trappingZoneSpray.spray_product.id,
                label: props.trappingZoneSpray.spray_product.product.name
              },

              // Regular inputs
              trapping_zone_id: props.trappingZoneSpray.trapping_zone_id,
              applied_date: props.trappingZoneSpray.applied_date,
              coverage_days: props.trappingZoneSpray.coverage_days,
              comments: props.trappingZoneSpray.comments,
              biofix: props.trappingZoneSpray.biofix
            }}
            validate={(values, props) => {
              const errors = {};
              const appliedYear = moment(values.applied_date).year();

              if (!values.sprayProduct) errors.sprayProduct = 'Required';
              if (!values.trapping_zone_id && values.trapping_zone_id !== 0)
                errors.trapping_zone_id = 'Required';
              if (!values.applied_date) errors.applied_date = 'Required';
              else if (appliedYear < databaseYear) {
                errors.applied_date = `The year must be ${databaseYear} or later.`;
              } else if (appliedYear > databaseYear + 1) {
                errors.applied_date = `The year cannot be larger than ${
                  databaseYear + 1
                }.`;
              } else if (!dateFormatRegex.test(values.applied_date)) {
                errors.applied_date =
                  'Invalid date. Please use the format "YYYY-MM-DD", and be careful of leap years.';
              }
              if (!values.coverage_days) errors.coverage_days = 'Required';

              return errors;
            }}
            onSubmit={(values, actions) => {
              fetch(
                `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-zone-spray/${props.trappingZoneSpray.id}`,
                {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${traplogStore.bearerToken}`,
                    SeasonalDatabase: traplogStore.selectedDatabase
                  },
                  body: JSON.stringify({
                    spray_product_id: values.sprayProduct.value,
                    trapping_zone_id: values.trapping_zone_id,
                    applied_date: values.applied_date,
                    coverage_days: values.coverage_days,
                    comments: values.comments,
                    biofix: values.biofix
                  })
                }
              )
                .then((res) => {
                  if (res.ok) return res.json();
                  else if (res.status === 401)
                    traplogStore.refreshBearerToken();
                  else throw Error(res.statusText);
                })
                .then((data) => {
                  traplogStore.loadTrappingZones(
                    traplogStore.bearerToken,
                    traplogStore.refreshBearerToken,
                    traplogStore.selectedDatabase,
                    props.propertiesStore.selectedPropertyId
                  );
                  hide();
                })
                .catch((err) => {
                  if (process.env.NODE_ENV === 'development')
                    console.error(err);
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }}
          >
            {(props) => (
              <Fragment>
                <TrappingZoneSprayForm {...props} />
                <Button
                  varient='primary'
                  type='button'
                  onClick={props.handleSubmit}
                >
                  Submit
                </Button>
                {props.isSubmitting ? <LoadingOverlay /> : null}
              </Fragment>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  }
}

EditTrappingZoneSprayModal.propTypes = {
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired,
  trappingZoneSpray: PropTypes.object.isRequired,
  setTrappingZoneSpray: PropTypes.func.isRequired
};

export default observer(EditTrappingZoneSprayModal);
